import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import VerificationInput from '../../components/VerificationInput'
import { getApiUrl, getAuthHeaders } from '../../config/api'

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    username: '',
    password: '',
    confirmPassword: '',
    role: 'student' as const
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [step, setStep] = useState(1) // 1: 填写信息, 2: 验证邮箱/手机
  const [verificationType, setVerificationType] = useState<'email' | 'phone' | null>(null)
  const [pendingRegistration, setPendingRegistration] = useState<any>(null)
  
  const navigate = useNavigate()

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.firstName.trim()) newErrors.firstName = '请输入姓'
    if (!formData.lastName.trim()) newErrors.lastName = '请输入名'
    if (!formData.email.trim()) {
      newErrors.email = '请输入邮箱'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = '请输入有效的邮箱地址'
    }
    if (!formData.phone.trim()) {
      newErrors.phone = '请输入手机号'
    } else if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = '请输入有效的手机号'
    }
    if (!formData.username.trim()) {
      newErrors.username = '请输入用户名'
    } else if (formData.username.length < 3) {
      newErrors.username = '用户名至少3个字符'
    }
    if (!formData.password) {
      newErrors.password = '请输入密码'
    } else if (formData.password.length < 6) {
      newErrors.password = '密码至少6个字符'
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = '请确认密码'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = '两次输入的密码不一致'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    // 保存注册数据，进入验证步骤
    setPendingRegistration({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      username: formData.username,
      password: formData.password,
      role: formData.role
    })
    
    // 确定验证方式（优先手机验证）
    if (formData.phone) {
      setVerificationType('phone')
    } else if (formData.email) {
      setVerificationType('email')
    }
    
    setStep(2) // 进入验证步骤
  }

  const performRegistration = async () => {
    if (!pendingRegistration) return
    
    setIsLoading(true)
    try {
      const response = await fetch(getApiUrl('/auth/register'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(pendingRegistration)
      })

      const data = await response.json()

      if (response.ok) {
        toast.success('注册成功！请登录')
        navigate('/login')
      } else {
        toast.error(data.message || '注册失败')
      }
    } catch (error) {
      toast.error('注册失败，请稍后重试')
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerificationSuccess = () => {
    performRegistration()
  }

  const handleVerificationCancel = () => {
    setStep(1)
    setVerificationType(null)
    setPendingRegistration(null)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value
    })
    // 清除对应字段的错误
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      })
    }
  }

  const benefits = [
    { title: '智能路径', desc: 'AI 生成学习路线，每周自动复盘' },
    { title: '导师陪伴', desc: '全球名校导师 1v1 解锁潜力' },
    { title: '项目成长', desc: '科研、实习、志愿者三线并进' },
  ]

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-800 py-12 px-4 sm:px-6 lg:px-12 overflow-hidden">
      {step === 2 && verificationType && pendingRegistration && (
        <VerificationInput
          type={verificationType}
          identifier={verificationType === 'phone' ? pendingRegistration.phone : pendingRegistration.email}
          purpose="register"
          onVerifySuccess={handleVerificationSuccess}
          onCancel={handleVerificationCancel}
        />
      )}

      <div className="absolute inset-0 opacity-60">
        <div className="absolute -top-24 -left-16 w-72 h-72 bg-blue-500/40 blur-[150px]" />
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-purple-600/30 blur-[200px]" />
        <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-cyan-500/30 blur-[180px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto grid lg:grid-cols-2 gap-10">
        {/* 左侧介绍 */}
        <div className="hidden lg:flex flex-col justify-between text-white bg-white/5 border border-white/10 rounded-3xl p-10 backdrop-blur-2xl shadow-2xl">
          <div>
            <p className="uppercase tracking-[0.4em] text-blue-200 text-xs mb-4">philiapedia</p>
            <h1 className="text-4xl font-bold leading-tight mb-6">
              加入新一代<br />AI 驱动的学习社群
            </h1>
            <p className="text-blue-100 text-base leading-relaxed">
              我们为每位学生打造“学术 + 项目 + 申请”的一体化成长体验。从首份规划到最终 Offer，都有导师与智能工具陪伴。
            </p>
          </div>
          <div className="space-y-6">
            {benefits.map(item => (
              <div key={item.title} className="flex items-start space-x-4">
                <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-blue-200" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-white">{item.title}</p>
                  <p className="text-blue-100 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
            {[
              { label: 'Offer 转化', value: '84%' },
              { label: '完成项目', value: '1.2k+' },
              { label: '导师团队', value: '320+' }
            ].map(stat => (
              <div key={stat.label}>
                <div className="text-3xl font-semibold">{stat.value}</div>
                <div className="text-sm text-blue-100">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 右侧表单 */}
        <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/40">
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold text-gray-900">创建学生账户</h2>
              <Link
                to="/login"
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                已有账号？立即登录
              </Link>
            </div>
            <p className="text-gray-500 mt-2">3 分钟完成注册，AI 帮你生成第一份留学规划。</p>
          </div>

          <div className="flex rounded-2xl bg-gray-100 p-1 mb-6">
            <button className="flex-1 py-2 text-center rounded-xl bg-white shadow text-sm font-medium text-gray-900">学生注册</button>
            <Link to="/teacher-register" className="flex-1 py-2 text-center rounded-xl text-sm font-medium text-gray-500 hover:text-gray-900">申请成为老师</Link>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
          {/* 姓名 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                姓
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  errors.firstName ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="张"
                value={formData.firstName}
                onChange={handleChange}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                名
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  errors.lastName ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="三"
                value={formData.lastName}
                onChange={handleChange}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* 邮箱 */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              邮箱地址
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* 手机号 */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
              手机号
            </label>
            <input
              id="phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              required
              className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                errors.phone ? 'border-red-300' : 'border-gray-300'
              } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              placeholder="13800138000"
              value={formData.phone}
              onChange={handleChange}
            />
            {errors.phone && (
              <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
            )}
          </div>

          {/* 用户名 */}
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              用户名
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              required
              className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                errors.username ? 'border-red-300' : 'border-gray-300'
              } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
              placeholder="用户名"
              value={formData.username}
              onChange={handleChange}
            />
            {errors.username && (
              <p className="mt-1 text-sm text-red-600">{errors.username}</p>
            )}
          </div>

          {/* 密码 */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              密码
            </label>
            <div className="mt-1 relative">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                  errors.password ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="至少6个字符"
                value={formData.password}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password}</p>
            )}
          </div>

          {/* 确认密码 */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              确认密码
            </label>
            <div className="mt-1 relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                  errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                placeholder="再次输入密码"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? '注册中...' : '注册账户'}
            </button>
          </div>

          <div className="text-xs text-gray-500 text-center">
            注册即表示您同意我们的{' '}
            <a href="#" className="text-blue-600 hover:text-blue-500">
              服务条款
            </a>{' '}
            和{' '}
            <a href="#" className="text-blue-600 hover:text-blue-500">
              隐私政策
            </a>
          </div>
        </form>
      </div>
    </div>
  </div>
  )
}

export default RegisterPage 